// src/pages/auth/ResetPassword.tsx
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, Shield, AlertCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  bg: string;
}

const getPasswordStrength = (password: string): PasswordStrength => {
  let score = 0;

  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 1, label: 'Lemah', color: 'text-danger', bg: 'bg-danger' };
  if (score === 2) return { score: 2, label: 'Sedang', color: 'text-warning', bg: 'bg-warning' };
  if (score === 3) return { score: 3, label: 'Kuat', color: 'text-info', bg: 'bg-info' };
  return { score: 4, label: 'Sangat Kuat', color: 'text-success', bg: 'bg-success' };
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [logoError, setLogoError] = useState(false);

  const { settings, fetchPublicSettings } = useSystemSettingsStore();

  // Fetch public settings on mount
  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  // Reset logo error when logo_url changes
  useEffect(() => {
    setLogoError(false);
  }, [settings.logo_url]);

  // Dynamic values from settings
  const appName = settings.app_name || 'SIM-EVAL';
  const appDescription = settings.app_description || 'Sistem Informasi Manajemen Evaluasi';
  const institutionName = settings.institution_name || 'Universitas Buana Perjuangan Karawang';
  const readableFooterText = settings.footer_text || 'Copyright 2026 SIM-EVAL UBP Karawang. Hak cipta dilindungi.';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const footerText = settings.footer_text || '© 2026 SIM-EVAL UBP Karawang. All rights reserved.';

  const logoUrl = getAssetUrl(settings.logo_url);
  const showLogo = logoUrl && !logoError;

  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Update document title and favicon
  useEffect(() => {
    if (appName) {
      document.title = `Reset Password - ${appName}`;
    }
    if (settings.favicon_url) {
      const faviconUrl = getAssetUrl(settings.favicon_url);
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }
  }, [appName, settings.favicon_url]);

  const strength = password ? getPasswordStrength(password) : null;

  const requirements = [
    { label: 'Minimal 8 karakter', met: password.length >= 8 },
    { label: 'Huruf besar & kecil', met: /[A-Z]/.test(password) && /[a-z]/.test(password) },
    { label: 'Mengandung angka', met: /[0-9]/.test(password) },
    { label: 'Karakter spesial (!@#$)', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!password) {
      newErrors.password = 'Password baru wajib diisi';
    } else if (password.length < 8) {
      newErrors.password = 'Password minimal 8 karakter';
    }

    if (!passwordConfirmation) {
      newErrors.password_confirmation = 'Konfirmasi password wajib diisi';
    } else if (password !== passwordConfirmation) {
      newErrors.password_confirmation = 'Password tidak cocok';
    }

    if (!token) {
      newErrors.token = 'Token reset tidak valid. Silakan minta link baru.';
    }

    if (!email) {
      newErrors.email = 'Email tidak ditemukan. Silakan minta link baru.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // ✅ FIXED: Uncomment actual API call
      await authService.resetPassword({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      setSuccess(true);
      toast.success('Password berhasil direset!');
    } catch (err: any) {
      console.error('Reset password error:', err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data?.errors || {};
        const mappedErrors: Record<string, string> = {};

        Object.keys(validationErrors).forEach((key) => {
          mappedErrors[key] = validationErrors[key][0] || validationErrors[key];
        });

        setErrors(mappedErrors);
      } else if (err.response?.status === 400 || err.response?.data?.message?.includes('token')) {
        setErrors({ token: 'Token sudah kadaluarsa atau tidak valid. Silakan minta link baru.' });
      } else {
        toast.error(err.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-dark via-teal-main to-teal-light flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - Consistent with Login */}
        <div className="text-center mb-8">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 mb-4 animate-bounce overflow-hidden rounded-3xl ${showLogo ? 'bg-transparent' : 'bg-white shadow-2xl'
              }`}
          >
            {showLogo ? (
              <img
                src={logoUrl!}
                alt="Logo"
                className="w-full h-full object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="text-3xl font-black bg-gradient-to-r from-teal-dark to-teal-main bg-clip-text text-transparent">
                {brandInitials}
              </span>
            )}
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight drop-shadow-lg">{appName}</h1>
          <p className="text-sm text-white/80 mt-2 font-medium">{appDescription}</p>
          <p className="text-xs text-white/70 mt-1">{institutionName}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {!success ? (
            <>
              {/* Form State */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-teal-main/10 rounded-xl mb-3">
                  <Shield className="w-6 h-6 text-teal-main" />
                </div>
                <h2 className="text-xl font-bold text-teal-dark">Reset Password</h2>
                <p className="text-sm text-neutral-text-secondary mt-1">
                  Buat password baru untuk akun{' '}
                  {email && <span className="font-semibold text-teal-main">{email}</span>}
                </p>
              </div>

              {/* Token/Email Error */}
              {(errors.token || errors.email) && (
                <div className="mb-4 p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-danger font-medium">{errors.token || errors.email}</p>
                    <Link to="/forgot-password" className="text-xs text-teal-main hover:underline mt-1 inline-block">
                      Minta link reset baru →
                    </Link>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* New Password */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    Password Baru
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-text-secondary" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                      }}
                      placeholder="Masukkan password baru"
                      className={`
                        w-full pl-10 pr-12 py-3 rounded-xl border text-sm
                        focus:outline-none focus:ring-2 focus:ring-teal-light
                        ${errors.password ? 'border-danger' : 'border-neutral-border'}
                      `}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text-secondary hover:text-neutral-text-primary"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-danger">{errors.password}</p>}

                  {/* Strength Meter */}
                  {password && strength && (
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 flex gap-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`h-1.5 flex-1 rounded-full transition-colors ${level <= strength.score ? strength.bg : 'bg-gray-200'
                                }`}
                            />
                          ))}
                        </div>
                        <span className={`text-xs font-semibold ${strength.color}`}>
                          {strength.label}
                        </span>
                      </div>

                      {/* Requirements */}
                      <div className="grid grid-cols-2 gap-1">
                        {requirements.map((req, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <CheckCircle
                              className={`w-3 h-3 ${req.met ? 'text-success' : 'text-neutral-border'}`}
                            />
                            <span
                              className={`text-[10px] ${req.met ? 'text-success' : 'text-neutral-text-secondary'
                                }`}
                            >
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    Konfirmasi Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-text-secondary" />
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                        if (errors.password_confirmation)
                          setErrors((prev) => ({ ...prev, password_confirmation: '' }));
                      }}
                      placeholder="Ulangi password baru"
                      className={`
                        w-full pl-10 pr-12 py-3 rounded-xl border text-sm
                        focus:outline-none focus:ring-2 focus:ring-teal-light
                        ${errors.password_confirmation ? 'border-danger' : 'border-neutral-border'}
                      `}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text-secondary hover:text-neutral-text-primary"
                    >
                      {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password_confirmation && (
                    <p className="text-xs text-danger">{errors.password_confirmation}</p>
                  )}

                  {/* Match indicator */}
                  {passwordConfirmation && (
                    <div className="flex items-center gap-1 mt-1">
                      {password === passwordConfirmation ? (
                        <>
                          <CheckCircle className="w-3 h-3 text-success" />
                          <span className="text-[10px] text-success">Password cocok</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-3 h-3 text-danger" />
                          <span className="text-[10px] text-danger">Password tidak cocok</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !token || !email}
                  className={`
                    w-full py-3 rounded-xl font-bold text-sm text-white
                    bg-gradient-to-r from-teal-dark to-teal-main
                    hover:from-teal-dark hover:to-teal-light
                    shadow-lg shadow-teal-main/40 hover:shadow-xl hover:shadow-teal-main/50
                    focus:outline-none focus:ring-2 focus:ring-teal-light focus:ring-offset-2
                    transition-all duration-300
                    ${loading || !token || !email ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                  `}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Menyimpan...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-success" />
                </div>
                <h2 className="text-xl font-bold text-teal-dark">Password Berhasil Direset!</h2>
                <p className="text-sm text-neutral-text-secondary mt-2 leading-relaxed">
                  Password Anda telah berhasil diperbarui. Silakan login dengan password baru.
                </p>

                <button
                  onClick={() => navigate('/login')}
                  className="mt-6 w-full py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-teal-dark to-teal-main hover:from-teal-dark hover:to-teal-light shadow-lg transition-all hover:scale-105"
                >
                  Masuk ke Sistem
                </button>
              </div>
            </>
          )}

          {/* Back to Login */}
          {!success && (
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm font-medium text-teal-main hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Kembali ke login
              </Link>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-white/50 mt-6 font-medium">
          {readableFooterText}
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
